import type { Conflict, ConflictCategory, CountryStats, Region } from './types';
import { conflicts } from './data/conflicts';
import { countryRegions } from './data/countries';

// ── Formatting ──────────────────────────────────────────

export function formatNumber(n: number, decimals = 0): string {
  return n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatDeaths(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}k`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1)}k`;
  return formatNumber(n);
}

export function conflictDuration(startYear: number): string {
  const years = new Date().getFullYear() - startYear;
  if (years < 1) return '< 1 year';
  if (years === 1) return '1 year';
  return `${years} years`;
}

// ── Category helpers ────────────────────────────────────

const categoryOrder: Record<ConflictCategory, number> = {
  major_war: 4,
  minor_war: 3,
  conflict: 2,
  skirmish: 1,
};

export function categoryLabel(cat: ConflictCategory): string {
  switch (cat) {
    case 'major_war': return 'Major War';
    case 'minor_war': return 'Minor War';
    case 'conflict': return 'Conflict';
    case 'skirmish': return 'Skirmish';
  }
}

export function categoryColor(cat: ConflictCategory): string {
  switch (cat) {
    case 'major_war': return '#dc2626';
    case 'minor_war': return '#ea580c';
    case 'conflict': return '#d97706';
    case 'skirmish': return '#65a30d';
  }
}

export function intensityScore(cat: ConflictCategory): number {
  return categoryOrder[cat];
}

export function intensityColor(score: number): string {
  if (score >= 4) return '#dc2626';
  if (score >= 3) return '#ea580c';
  if (score >= 2) return '#d97706';
  if (score >= 1) return '#65a30d';
  return '#1e293b';
}

export function mapFillColor(score: number): string {
  if (score >= 4) return '#7f1d1d';   // major war
  if (score >= 3) return '#9a3412';   // minor war
  if (score >= 2) return '#78350f';   // conflict
  if (score >= 1) return '#365314';   // skirmish
  return '#1e293b';                    // peaceful
}

export function mapFillOpacity(score: number): number {
  if (score >= 4) return 0.85;
  if (score >= 3) return 0.7;
  if (score >= 2) return 0.55;
  if (score >= 1) return 0.4;
  return 0.15;
}

// ── Data aggregation ────────────────────────────────────

export function computeCountryStats(): Map<string, CountryStats> {
  const map = new Map<string, CountryStats>();

  for (const c of conflicts) {
    for (let i = 0; i < c.countries.length; i++) {
      const iso3 = c.countries[i];
      const name = c.countryNames[i];
      const existing = map.get(iso3);
      if (existing) {
        existing.conflictCount++;
        existing.conflicts.push(c.id);
        existing.totalDeaths2025 += c.deaths2025;
        if (categoryOrder[c.category] > categoryOrder[existing.maxCategory]) {
          existing.maxCategory = c.category;
        }
        existing.intensityScore = Math.max(existing.intensityScore, intensityScore(c.category));
      } else {
        map.set(iso3, {
          iso3,
          name,
          region: countryRegions[iso3] || 'Africa',
          conflictCount: 1,
          conflicts: [c.id],
          totalDeaths2025: c.deaths2025,
          maxCategory: c.category,
          intensityScore: intensityScore(c.category),
        });
      }
    }
  }

  return map;
}

export function getConflictsByRegion(): Record<Region, Conflict[]> {
  const result: Record<Region, Conflict[]> = {
    'Africa': [],
    'Americas': [],
    'Asia': [],
    'Europe': [],
    'Middle East': [],
    'Oceania': [],
  };
  for (const c of conflicts) {
    result[c.region].push(c);
  }
  return result;
}

export function getConflictById(id: string): Conflict | undefined {
  return conflicts.find(c => c.id === id);
}

export function sortConflicts(
  list: Conflict[],
  sortBy: 'deaths' | 'duration' | 'name' | 'category',
  ascending = false
): Conflict[] {
  const sorted = [...list];
  sorted.sort((a, b) => {
    let cmp = 0;
    switch (sortBy) {
      case 'deaths':
        cmp = a.deaths2025 - b.deaths2025;
        break;
      case 'duration':
        cmp = a.startYear - b.startYear;
        break;
      case 'name':
        cmp = a.name.localeCompare(b.name);
        break;
      case 'category':
        cmp = categoryOrder[a.category] - categoryOrder[b.category];
        break;
    }
    return ascending ? cmp : -cmp;
  });
  return sorted;
}

export function filterConflicts(
  list: Conflict[],
  filters: { region?: Region; category?: ConflictCategory; search?: string }
): Conflict[] {
  return list.filter(c => {
    if (filters.region && c.region !== filters.region) return false;
    if (filters.category && c.category !== filters.category) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      return (
        c.name.toLowerCase().includes(q) ||
        c.countryNames.some(n => n.toLowerCase().includes(q)) ||
        c.sideA.some(s => s.toLowerCase().includes(q)) ||
        c.sideB.some(s => s.toLowerCase().includes(q))
      );
    }
    return true;
  });
}

// ── Insights ────────────────────────────────────────────

export interface Insight {
  title: string;
  description: string;
  severity: 'info' | 'warning' | 'alert';
  conflictId?: string;
}

export function generateInsights(): Insight[] {
  const insights: Insight[] = [];
  const countryStats = computeCountryStats();

  // Longest-running conflicts
  const longest = [...conflicts].sort((a, b) => a.startYear - b.startYear).slice(0, 3);
  for (const c of longest) {
    insights.push({
      title: `${c.name}: ${conflictDuration(c.startYear)} of conflict`,
      description: `Started in ${c.startYear}. ${c.description.split('.')[0]}.`,
      severity: 'info',
      conflictId: c.id,
    });
  }

  // Deadliest conflicts (2025)
  const deadliest = [...conflicts].sort((a, b) => b.deaths2025 - a.deaths2025).slice(0, 3);
  for (const c of deadliest) {
    insights.push({
      title: `${c.name}: ~${formatDeaths(c.deaths2025)} fatalities in 2025`,
      description: `The ${categoryLabel(c.category).toLowerCase()} in ${c.countryNames[0]} is among the world's deadliest active conflicts.`,
      severity: 'alert',
      conflictId: c.id,
    });
  }

  // Countries with multiple conflicts
  for (const [, stats] of countryStats) {
    if (stats.conflictCount >= 3) {
      insights.push({
        title: `${stats.name}: ${stats.conflictCount} simultaneous conflicts`,
        description: `This country is involved in ${stats.conflictCount} distinct armed conflicts — one of the most conflict-affected nations in the world.`,
        severity: 'warning',
      });
    }
  }

  // Major wars count
  const majorWars = conflicts.filter(c => c.category === 'major_war');
  insights.push({
    title: `${majorWars.length} major wars active worldwide`,
    description: `Conflicts causing 10,000+ fatalities per year. The highest number of simultaneous major wars since the end of the Cold War.`,
    severity: 'alert',
  });

  // Total conflict count
  insights.push({
    title: `${conflicts.length} active armed conflicts tracked`,
    description: `ConflictMap tracks every known armed conflict worldwide — from major wars to simmering border skirmishes. This is the most concurrent conflicts since WWII.`,
    severity: 'info',
  });

  // Regions
  const byRegion = getConflictsByRegion();
  const regionEntries = Object.entries(byRegion) as [Region, Conflict[]][];
  const worstRegion = regionEntries.sort((a, b) => b[1].length - a[1].length)[0];
  insights.push({
    title: `${worstRegion[0]}: ${worstRegion[1].length} active conflicts`,
    description: `The most conflict-affected region in the world, driven by ${worstRegion[1].filter(c => c.category === 'major_war').length} major wars and ${worstRegion[1].filter(c => c.category === 'minor_war').length} minor wars.`,
    severity: 'warning',
  });

  return insights;
}
